import { brands as compiledBrands } from 'virtual:today-brands'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { BrandRuntimeArtifact } from '@today/brand-config/runtime'
import { useEffect, useMemo, useState } from 'react'
import { platformBrandService } from '@/services/platform-brand.service'
import { PLATFORM_BRAND_STORAGE_KEY, resolvePlatformBrandId } from './resolve-platform-brand-id'

/** The pointer's TanStack Query key; the switcher writes its saved value here (TO-541 D11). */
export const PLATFORM_BRAND_QUERY_KEY = ['platform-brand'] as const

/** How long a fetched pointer is fresh; a window focus after that re-fetches it (REQ-102). */
const POINTER_STALE_MS = 60_000

/** `localStorage` access never breaks the binding (Safari private mode throws). */
const readStored = (): string | null => {
	try {
		return localStorage.getItem(PLATFORM_BRAND_STORAGE_KEY)
	} catch {
		return null
	}
}

/** Remembers the last usable pointer, best effort. */
export const writeStoredPlatformBrandId = (brandId: string): void => {
	try {
		localStorage.setItem(PLATFORM_BRAND_STORAGE_KEY, brandId)
	} catch {
		// Storage unavailable: the next load falls back to the built-in default.
	}
}

/**
 * The runtime platform brand id (TO-541 REQ-102): `GET /platform/brand` when its
 * answer is compiled, else the last-known compiled id from `localStorage`, else
 * `aurora` — also while the read is pending or after it failed. A usable answer
 * is remembered; the pointer re-fetches on window focus once it is 60 s old.
 */
export function usePlatformBrandId(brands: readonly BrandRuntimeArtifact[] = compiledBrands): string {
	const compiledIds = useMemo(() => brands.filter((brand) => brand.valid).map((brand) => brand.id), [brands])
	const [stored] = useState(readStored)
	const { data } = useQuery({
		queryKey: PLATFORM_BRAND_QUERY_KEY,
		queryFn: platformBrandService.get,
		staleTime: POINTER_STALE_MS,
		refetchOnWindowFocus: true,
		retry: false,
	})
	const queryClient = useQueryClient()
	// TanStack Query v5 refetches on `visibilitychange` only; REQ-102 also covers a
	// plain window focus (another app → this visible tab). Stale-only, so a focus
	// inside the 60 s window costs nothing.
	useEffect(() => {
		const onFocus = () => {
			void queryClient.refetchQueries({ queryKey: PLATFORM_BRAND_QUERY_KEY, stale: true, type: 'active' })
		}
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [queryClient])
	const fetched = data?.brandId
	useEffect(() => {
		if (fetched && compiledIds.includes(fetched)) writeStoredPlatformBrandId(fetched)
	}, [fetched, compiledIds])
	return resolvePlatformBrandId(fetched, stored, compiledIds)
}
